import { initializeApp } from "firebase/app";
import { getDatabase, ref, onValue, set, remove, connectDatabaseEmulator } from "firebase/database";
import { initializeFirestore, connectFirestoreEmulator, memoryLocalCache, persistentLocalCache } from "firebase/firestore";
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from "firebase/auth";
import { getFirebaseAuthDomain, getFirebaseProjectId } from "./utils/firebaseProject";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: getFirebaseAuthDomain(),
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: getFirebaseProjectId(),
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Validate required Firebase config
if (!firebaseConfig.apiKey) {
  console.error('❌ Firebase API key is missing! Check your environment variables.');
  throw new Error('Firebase API key is required');
}

// Use emulators when explicitly enabled or when environment is 'emulator', and never in production
const isProduction = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENVIRONMENT === 'production';
const emulatorExplicitlyEnabled = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true';
const isEmulatorEnvironment = process.env.REACT_APP_ENVIRONMENT === 'emulator';
const shouldUseEmulators = (emulatorExplicitlyEnabled || isEmulatorEnvironment) && !isProduction;

// Initialize Firebase app
const app = initializeApp(firebaseConfig);

// Initialize services
const database = getDatabase(app);
// Use explicit cache config to avoid "INTERNAL ASSERTION FAILED: Unexpected state (ca9)"
// which occurs when multiple onSnapshot listeners hit a race condition in the watch stream.
// Emulator uses memoryLocalCache; production uses persistentLocalCache (explicit = stable).
const db = shouldUseEmulators
  ? initializeFirestore(app, { localCache: memoryLocalCache() })
  : initializeFirestore(app, { localCache: persistentLocalCache() });
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

// Verify we're not accidentally using emulator URLs
console.log('🔍 Firebase Service URLs:', {
  authDomain: firebaseConfig.authDomain,
  databaseURL: firebaseConfig.databaseURL,
  projectId: firebaseConfig.projectId
});

// Configure Google Auth Provider
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

console.log('🔍 Firebase Environment Check:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
  REACT_APP_USE_FIREBASE_EMULATOR: process.env.REACT_APP_USE_FIREBASE_EMULATOR,
  isProduction: isProduction,
  shouldUseEmulators: shouldUseEmulators
});

// NEVER connect to emulators in production - be very strict
if (shouldUseEmulators && !isProduction) {
  try {
    console.log("🔧 Connecting to Firebase emulators (development only)...");
    // Connect to Firestore emulator
    connectFirestoreEmulator(db, '127.0.0.1', 5001);
    // Connect to Realtime Database emulator
    connectDatabaseEmulator(database, '127.0.0.1', 9000);
    // Connect to Auth emulator for development
    connectAuthEmulator(auth, 'http://127.0.0.1:9099');
    console.log("✅ Connected to local Firebase emulators.");
  } catch (error) {
    console.log("⚠️ Firebase emulators already connected or not available:", error.message);
  }
} else {
  console.log("🚀 Using Firebase production services (emulators disabled).");
  console.log("Firebase Config:", {
    apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.substring(0, 10)}...` : 'NOT SET',
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    appId: firebaseConfig.appId ? `${firebaseConfig.appId.substring(0, 10)}...` : 'NOT SET'
  });
}

export { app, database, db, auth, googleProvider, ref, onValue, set, remove };
